import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { NotificationService } from '../services';
import { NOTIFICATION_USER_KEY, NOTIFICATION_COUNT_KEY, NOTIFICATION_LIST_KEY } from '../constants/query';
import { INotification } from '../interfaces/INotification';

interface UseMarkAsReadOnlyOptions {
  showToast?: boolean;
  onSuccess?: () => void;
  onError?: (error: any) => void;
}

export const useMarkAsReadOnly = (options?: UseMarkAsReadOnlyOptions) => {
  const { showToast = false, onSuccess, onError } = options || {};
  const queryClient = useQueryClient();
  const { t } = useTranslation('notification');

  return useMutation({
    mutationFn: ({ fromId, toId }: { fromId: string; toId: string }) => NotificationService.markAsReadRange(fromId, toId),
    onMutate: async ({ fromId, toId }) => {
      await queryClient.cancelQueries({ queryKey: [NOTIFICATION_USER_KEY] });
      await queryClient.cancelQueries({ queryKey: [NOTIFICATION_COUNT_KEY] });
      await queryClient.cancelQueries({ queryKey: [NOTIFICATION_LIST_KEY] });

      const previousNotifications = queryClient.getQueryData([NOTIFICATION_USER_KEY]);

      queryClient.setQueriesData({ queryKey: [NOTIFICATION_USER_KEY] }, (oldData: any) => {
        if (!oldData?.data) return oldData;

        const data = oldData.data;
        let startIndex = -1;
        let endIndex = -1;

        // Find the indices of fromId and toId
        for (let i = 0; i < data.length; i++) {
          if (data[i]._id === fromId) startIndex = i;
          if (data[i]._id === toId) endIndex = i;
        }

        if (startIndex === -1 || endIndex === -1 || startIndex > endIndex) return oldData;

        // Mark the range as read
        const updatedData = data.map((n: INotification, index: number) => {
          if (index >= startIndex && index <= endIndex) {
            return { ...n, viewed: true };
          }
          return n;
        });

        return {
          ...oldData,
          data: updatedData,
        };
      });

      return { previousNotifications };
    },
    onError: (error, { fromId, toId }, context) => {
      if (context?.previousNotifications) {
        queryClient.setQueryData([NOTIFICATION_USER_KEY], context.previousNotifications);
      }

      if (showToast) {
        toast.error(t('errorMarkingAsRead'));
      }

      onError?.(error);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [NOTIFICATION_USER_KEY] });
      queryClient.invalidateQueries({ queryKey: [NOTIFICATION_COUNT_KEY] });
      queryClient.invalidateQueries({ queryKey: [NOTIFICATION_LIST_KEY] });

      onSuccess?.();
    },
  });
};
